var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from "react";
import { Table, Tag } from "antd";
import { SEVERITY_COLORS } from "./types";
function formatTimestamp(ts, utc) {
    try {
        var d = new Date(ts);
        var pad = function (n, len) {
            if (len === void 0) { len = 2; }
            return String(n).padStart(len, "0");
        };
        if (utc) {
            return "".concat(d.getUTCFullYear(), "-").concat(pad(d.getUTCMonth() + 1), "-").concat(pad(d.getUTCDate()), " ").concat(pad(d.getUTCHours()), ":").concat(pad(d.getUTCMinutes()), ":").concat(pad(d.getUTCSeconds()), ".").concat(pad(d.getUTCMilliseconds(), 3), "Z");
        }
        var hours = d.getHours();
        var ampm = hours >= 12 ? "PM" : "AM";
        var h12 = hours % 12 || 12;
        return "".concat(d.getFullYear(), "-").concat(pad(d.getMonth() + 1), "-").concat(pad(d.getDate()), " ").concat(h12, ":").concat(pad(d.getMinutes()), ":").concat(pad(d.getSeconds()), ".").concat(pad(d.getMilliseconds(), 3), " ").concat(ampm);
    }
    catch (_a) {
        return ts;
    }
}
function severityTagColor(level) {
    var upper = (level || "").toUpperCase();
    return SEVERITY_COLORS[upper] || SEVERITY_COLORS.INFO;
}
function buildColumns(useUtc) {
    return [
        {
            title: "Severity",
            dataIndex: "level",
            key: "level",
            // "Severity" needs 56px, the filter trigger 20px and the cell padding
            // 16px, so 90px left the header 2px short and it wrapped to two lines.
            // 110px clears it with room to spare for a heavier or wider header font.
            width: 110,
            render: function (level) {
                var upper = (level || "").toUpperCase();
                return (_jsx(Tag, { color: severityTagColor(level), style: { margin: 0, fontWeight: 600, fontSize: 11 }, children: upper }));
            },
            filters: [
                { text: "INFO", value: "INFO" },
                { text: "ERROR", value: "ERROR" },
            ],
            onFilter: function (value, record) {
                return (record.level || "").toUpperCase() === value;
            },
        },
        {
            title: "Timestamp",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 210,
            render: function (ts) { return (_jsx("span", { style: { fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }, children: formatTimestamp(ts, useUtc) })); },
            sorter: function (a, b) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            },
            defaultSortOrder: "descend",
        },
        {
            title: "Message",
            dataIndex: "message",
            key: "message",
            ellipsis: true,
            render: function (msg) { return (_jsx("span", { style: {
                    fontFamily: "monospace",
                    fontSize: 12,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                }, children: msg })); },
        },
    ];
}
function tryParseJson(str) {
    var s = str.trim();
    // Try strict JSON first
    try {
        return JSON.parse(s);
    }
    catch (_a) {
        // fall through
    }
    // Try relaxed: add quotes around unquoted keys and single-quoted values
    try {
        var fixed = s
            // Quote unquoted keys: word characters before a colon
            .replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":')
            // Replace single-quoted string values with double-quoted
            .replace(/:\s*'([^']*)'/g, ': "$1"');
        return JSON.parse(fixed);
    }
    catch (_b) {
        return null;
    }
}
function formatMessage(msg) {
    // Try to detect and pretty-print JSON / JSON-like content in the message
    var trimmed = (msg || "").trim();
    // Case 1: entire message is JSON or JSON-like
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
        var parsed = tryParseJson(trimmed);
        if (parsed) {
            return JSON.stringify(parsed, null, 2);
        }
    }
    // Case 2: message contains embedded JSON/JSON-like substring(s)
    // Match brace-delimited or bracket-delimited blocks within the text
    var jsonPattern = /(\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}|\[[^\[\]]*(?:\[[^\[\]]*\][^\[\]]*)*\])/g;
    var parts = [];
    var lastIndex = 0;
    var match;
    var foundJson = false;
    while ((match = jsonPattern.exec(trimmed)) !== null) {
        var parsed = tryParseJson(match[0]);
        if (parsed) {
            foundJson = true;
            // Add text before the JSON block
            if (match.index > lastIndex) {
                parts.push(trimmed.slice(lastIndex, match.index));
            }
            // Add formatted JSON
            parts.push(JSON.stringify(parsed, null, 2));
            lastIndex = match.index + match[0].length;
        }
    }
    if (foundJson) {
        // Add any trailing text
        if (lastIndex < trimmed.length) {
            parts.push(trimmed.slice(lastIndex));
        }
        return parts.join("");
    }
    return msg;
}
var LogTable = function (_a) {
    var logs = _a.logs, loading = _a.loading, useUtc = _a.useUtc;
    var columns = useMemo(function () { return buildColumns(useUtc); }, [useUtc]);
    // Add unique keys and promote SERVICE_ERROR messages to ERROR severity
    var dataSource = logs.map(function (entry, idx) { return (__assign(__assign({}, entry), { level: /(SERVICE_ERROR|HTTP_ERROR|VALIDATION_ERROR)/.test(entry.message || "") ? "ERROR" : entry.level, _key: "".concat(entry.createdAt, "-").concat(idx) })); });
    return (_jsxs("div", { className: "card", style: { marginBottom: 0, overflow: "hidden" }, children: [_jsx("style", { children: "\n        .compact-log-table .ant-table-cell {\n          padding: 4px 8px !important;\n          line-height: 1.3 !important;\n        }\n        /* Column headers are fixed-width, so a header that outgrows its column\n           should show that plainly rather than silently wrapping to two lines\n           and pushing the header row taller. */\n        .compact-log-table .ant-table-thead .ant-table-cell {\n          white-space: nowrap;\n        }\n        /* Ant tints the whole sorted column #fafafa. Timestamp is the default\n           sort, so that painted a 210px grey stripe down the entire height of\n           the table - it read as a stray background rather than a sort cue,\n           and its square ends fought the card's rounded corners. The header\n           arrows already indicate the sort, so drop the column fill and let\n           the sorted header match its neighbours. */\n        .compact-log-table td.ant-table-column-sort {\n          background: transparent;\n        }\n        .compact-log-table .ant-table-thead > tr > th.ant-table-column-sort {\n          background: #fafafa;\n        }\n        .compact-log-table .ant-table-row {\n          cursor: pointer;\n        }\n      " }), _jsx("div", { className: "card-body", style: { padding: 0 }, children: _jsx(Table, { dataSource: dataSource, columns: columns, rowKey: "_key", size: "small", loading: loading, pagination: false, tableLayout: "fixed", className: "compact-log-table", expandable: {
                        expandedRowRender: function (record) { return (_jsxs("div", { style: { padding: "8px 12px" }, children: [_jsxs("div", { style: { marginBottom: 6 }, children: [_jsx("strong", { children: "Level:" }), " ", _jsx(Tag, { color: severityTagColor(record.level), style: { fontWeight: 600 }, children: (record.level || "").toUpperCase() })] }), _jsxs("div", { style: { marginBottom: 6 }, children: [_jsx("strong", { children: "Timestamp:" }), " ", _jsx("span", { style: { fontFamily: "monospace" }, children: formatTimestamp(record.createdAt, useUtc) })] }), _jsxs("div", { children: [_jsx("strong", { children: "Message:" }), _jsx("pre", { style: {
                                                fontFamily: "monospace",
                                                fontSize: 12,
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                overflowWrap: "break-word",
                                                background: "#f5f5f5",
                                                padding: 10,
                                                borderRadius: 4,
                                                marginTop: 4,
                                                maxHeight: 400,
                                                overflow: "auto",
                                            }, children: formatMessage(record.message) })] })] })); },
                        rowExpandable: function () { return true; },
                    }, style: { fontSize: 12 }, locale: {
                        emptyText: loading ? "Loading logs..." : "No log entries found",
                    } }) })] }));
};
export default LogTable;
